import { observer } from "mobx-react-lite";
import styled from "@emotion/styled";
import { useAppStore } from "@/store";
import {
  MDSModal,
  MDSDialog,
  MDSDialogSlotName,
  MDSButton,
  MDSButtonVariant,
  MDSText,
  MDSTextVariant,
  MDSTextWeight,
  MDSTextColor,
  MDSTextField,
  MDSTextFieldSlotName,
  MDSTextFieldVariant,
  MDSIcon,
  MDSIconKind,
  MDSIconColor,
  MDSMenuDropdownSlotName,
  MDSButtonSlotName,
  MDSSeparator,
  MDSCheckbox,
} from "@/mds";
import { Virtuoso } from "react-virtuoso";
import { NoteListItem } from "./note-list-item";
import { SortByKind } from "@/modules/lenses/types";
import { useState, useEffect, useRef, useMemo } from "react";
import { trackEvent } from "@/domains/metrics/segment";
import { TrackedEvent } from "@/domains/metrics/tracked-event";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import type { ImportableNote } from "@/store/one-click-import/types";
import { SectionSelectionState } from "@/store/one-click-import/types";
import { type ImportedNoteSortBy } from "@/store/one-click-import/AppStoreOneClickImportStore";
import { ImportStatus } from "@/store/one-click-import/types";
import type { FC } from "react";
import { lensModule } from "@/modules/lenses";
import { SortByDropdownButton } from "../filters/sort-by-dropdown-button";
import { MDSToggle } from "@/mds/components/forms/toggle";
import { CollectionSelectorDropdown } from "./collection-selector-dropdown";
import { pluralize } from "@/modules/pluralize";
import { ImportEmptyState } from "./import-empty-state";
import { ImportLoadingSkeleton } from "./import-loading-skeleton";
import { ImportModalHeader } from "./import-modal-header";
import { ImportFooterError } from "./import-footer-error";

type ListItem =
  | { type: "section"; section: string; displayLabel: string }
  | { type: "note"; note: ImportableNote };

const SORT_OPTIONS: ImportedNoteSortBy[] = [
  SortByKind.LastModified,
  SortByKind.LastCreated,
  SortByKind.Alphabetical,
];
export const OneClickImportModal: FC = observer(function OneClickImportModal() {
  const { store } = useAppStore();
  const [isImporting, setIsImporting] = useState(false);
  const hasTrackedListView = useRef(false);

  const isModalOpen = store.oneClickImport.isModalOpen;
  const isLoading = store.oneClickImport.isLoading;
  const hasLoadingError = store.oneClickImport.hasLoadingError;
  const errorMessage = store.oneClickImport.errorMessage;
  const totalCount = store.oneClickImport.totalNotesCount;
  const selectableCount = store.oneClickImport.selectableNotesCount;
  const lockedCount = totalCount - selectableCount;

  const groupedNotes = store.oneClickImport.notesGroupedByFolder;
  const filteredNotes = store.oneClickImport.filteredNotes;

  // Flatten grouped notes into a list with section headers
  // If there are no folders at all, show notes without sections
  const flatList = useMemo(() => {
    const items: ListItem[] = [];

    // If there are no groups (no folders at all), show notes without section headers
    if (groupedNotes.length === 0) {
      filteredNotes.forEach(note => {
        items.push({ type: "note", note });
      });
      return items;
    }

    // Otherwise, show notes grouped by folder sections
    groupedNotes.forEach(group => {
      items.push({
        type: "section",
        section: group.section,
        displayLabel: group.section || "Unfiled",
      });
      group.notes.forEach(note => {
        items.push({ type: "note", note });
      });
    });

    return items;
  }, [groupedNotes, filteredNotes]);

  // Track list view when modal opens with notes
  useEffect(() => {
    if (isModalOpen && totalCount > 0 && !hasTrackedListView.current) {
      hasTrackedListView.current = true;
      trackEvent(TrackedEvent.OneClickImportListView, {
        total_notes_count: totalCount,
        selectable_notes_count: selectableCount,
        locked_notes_count: lockedCount,
      });
    }

    // Reset tracking flag when modal closes
    if (!isModalOpen) {
      hasTrackedListView.current = false;
    }
  }, [isModalOpen, totalCount, selectableCount, lockedCount]);

  const handleClose = () => {
    if (!isImporting && !isLoading) {
      store.oneClickImport.clearErrorMessage();
      trackEvent(TrackedEvent.OneClickImportCancel, {
        selected_count: store.oneClickImport.selectedNotesCount,
        total_count: totalCount,
      });
      store.oneClickImport.closeModal({ status: ImportStatus.Failed });
    }
  };

  const handleRetryLoadNotes = () => {
    store.oneClickImport.clearErrorMessage();
    store.oneClickImport.retryLoadNotes();
  };

  const handleFilterChange = (value: string) => {
    store.oneClickImport.setFilterQuery(value);
  };

  const handleClearFilter = () => {
    store.oneClickImport.setFilterQuery("");
  };

  const handleSortChange = (sortBy: SortByKind) => {
    // Only accept valid sort options for imported notes
    if (SORT_OPTIONS.includes(sortBy as ImportedNoteSortBy)) {
      store.oneClickImport.setSortOption(sortBy as ImportedNoteSortBy);
    }
  };

  const handleImport = async () => {
    // Clear any previous error when attempting a new import
    store.oneClickImport.clearErrorMessage();

    const notesToImport = store.oneClickImport.selectedNotes;

    if (notesToImport.length === 0) {
      store.oneClickImport.setErrorMessage("Please select at least one note to import");
      return;
    }

    trackEvent(TrackedEvent.OneClickImportSubmit, {
      selected_count: notesToImport.length,
      total_count: totalCount,
      use_folders_as_collections: store.oneClickImport.useFoldersAsCollections,
    });

    try {
      setIsImporting(true);

      await store.oneClickImport.importSelectedNotes();

      store.oneClickImport.closeModal({ status: ImportStatus.Success });
      store.oneClickImport.clear();
    } catch (error) {
      logger.error({
        message: "[OneClickImport] Import failed",
        info: { error: objectModule.safeErrorAsJson(error as Error) },
      });
      store.oneClickImport.setErrorMessage("Failed to import notes. Please try again.");
    } finally {
      setIsImporting(false);
    }
  };

  const selectedCount = store.oneClickImport.selectedNotesCount;
  const hasNotes = totalCount > 0;

  const renderItem = (_index: number, item: ListItem) => {
    if (item.type === "section") {
      // Use the actual section value for store calls, not the display label
      const selectionState = store.oneClickImport.getSectionSelectionState(item.section);

      const handleSectionToggle = () => {
        store.oneClickImport.toggleSectionSelection(item.section);
      };

      return (
        <StyledSectionHeader>
          <MDSCheckbox
            isSelected={selectionState === SectionSelectionState.All}
            isIndeterminate={selectionState === SectionSelectionState.Indeterminate}
            onChange={handleSectionToggle}
          >
            <MDSText
              color={MDSTextColor.Secondary}
              variant={MDSTextVariant.TextXs}
              weight={MDSTextWeight.SemiBold}
            >
              {item.displayLabel}
            </MDSText>
          </MDSCheckbox>
        </StyledSectionHeader>
      );
    }
    return <NoteListItem key={item.note.id} note={item.note} />;
  };

  return (
    <MDSModal
      isOpen={store.oneClickImport.isModalOpen}
      handleClose={handleClose}
      isDismissable={false}
    >
      <StyledWideDialog
        isOpen={store.oneClickImport.isModalOpen}
        handleClose={handleClose}
        isDismissable={!isImporting && !isLoading}
      >
        <ImportModalHeader slot={MDSDialogSlotName.Header} />

        <StyledContent slot={MDSDialogSlotName.Content}>
          <StyledFilterContainer>
            <MDSTextField
              variant={MDSTextFieldVariant.Default}
              placeholder="Filter by title"
              value={store.oneClickImport.filterQuery}
              onChange={handleFilterChange}
              onClearText={handleClearFilter}
              showClearTextButton
              aria-label="Filter notes by title"
            >
              <MDSIcon
                slot={MDSTextFieldSlotName.LeftAddon}
                kind={MDSIconKind.Search}
                color={MDSIconColor.Secondary}
              />
            </MDSTextField>
            <SortByDropdownButton
              sortOptions={SORT_OPTIONS}
              defaultSortOption={SortByKind.LastModified}
              selectedSortOption={store.oneClickImport.sortOption}
              onSelectSortOption={handleSortChange}
              customTrigger={(isOpen: boolean) => (
                <MDSButton
                  slot={MDSMenuDropdownSlotName.Trigger}
                  variant={MDSButtonVariant.Transparent}
                >
                  {lensModule.sortKindLabelMap[store.oneClickImport.sortOption]}

                  {isOpen ? (
                    <MDSIcon slot={MDSButtonSlotName.RightIcon} kind={MDSIconKind.AngleUp} />
                  ) : (
                    <MDSIcon slot={MDSButtonSlotName.RightIcon} kind={MDSIconKind.AngleDown} />
                  )}
                </MDSButton>
              )}
            />
          </StyledFilterContainer>
          <StyledContentWrapper>
            {isLoading ? (
              <ImportLoadingSkeleton />
            ) : hasNotes && flatList.length > 0 ? (
              <StyledVirtuosoWrapper>
                <Virtuoso
                  data={flatList}
                  itemContent={renderItem}
                  style={{
                    height: "400px",
                    scrollbarWidth: "none",
                    transition: "opacity 0.1s ease-in-out",
                  }}
                />
              </StyledVirtuosoWrapper>
            ) : (
              <ImportEmptyState totalNotesCount={totalCount} />
            )}
          </StyledContentWrapper>

          {!isLoading &&
            !hasLoadingError &&
            hasNotes &&
            (store.oneClickImport.tags.length > 0 || store.oneClickImport.folders.length > 0) && (
              <>
                <MDSSeparator />
                <StyledCollectionOptionsContainer>
                  {store.oneClickImport.tags.length > 0 && (
                    <StyledCollectionOptionRow>
                      <StyledToggleContainer>
                        <MDSToggle
                          checked={store.oneClickImport.useTagsAsCollections}
                          onChange={store.oneClickImport.toggleUseTagsAsCollections}
                        />
                        <MDSText variant={MDSTextVariant.TextS}>
                          Convert {store.oneClickImport.tags.length} tags to collections
                        </MDSText>
                      </StyledToggleContainer>
                      <CollectionSelectorDropdown
                        label="Choose specific tags"
                        items={store.oneClickImport.filteredTags}
                        selectedItems={store.oneClickImport.selectedTagNames}
                        onSelectionChange={store.oneClickImport.setSelectedTagNames}
                        isDisabled={!store.oneClickImport.useTagsAsCollections}
                        getCollectionId={store.oneClickImport.getCollectionIdForTag}
                        getNotesCount={store.oneClickImport.getNotesCountForTag}
                        searchQuery={store.oneClickImport.tagSearchQuery}
                        onSearchQueryChange={store.oneClickImport.setTagSearchQuery}
                      />
                    </StyledCollectionOptionRow>
                  )}

                  {store.oneClickImport.folders.length > 0 && (
                    <StyledCollectionOptionRow>
                      <StyledToggleContainer>
                        <MDSToggle
                          checked={store.oneClickImport.useFoldersAsCollections}
                          onChange={store.oneClickImport.toggleUseFoldersAsCollections}
                        />
                        <MDSText variant={MDSTextVariant.TextS}>
                          Convert {store.oneClickImport.folders.length} folders to collections
                        </MDSText>
                      </StyledToggleContainer>
                      <CollectionSelectorDropdown
                        label="Choose specific folders"
                        items={store.oneClickImport.filteredFolders}
                        selectedItems={store.oneClickImport.selectedFolderNames}
                        onSelectionChange={store.oneClickImport.setSelectedFolderNames}
                        isDisabled={!store.oneClickImport.useFoldersAsCollections}
                        getCollectionId={store.oneClickImport.getCollectionIdForFolder}
                        getNotesCount={store.oneClickImport.getNotesCountForFolder}
                        searchQuery={store.oneClickImport.folderSearchQuery}
                        onSearchQueryChange={store.oneClickImport.setFolderSearchQuery}
                      />
                    </StyledCollectionOptionRow>
                  )}
                </StyledCollectionOptionsContainer>
              </>
            )}
        </StyledContent>

        <StyledFooter slot={MDSDialogSlotName.Footer}>
          <StyledFooterLeft>
            <ImportFooterError message={errorMessage} />
          </StyledFooterLeft>
          {hasLoadingError ? (
            <MDSButton
              variant={MDSButtonVariant.Contrast}
              onClick={handleRetryLoadNotes}
              isLoading={isLoading}
            >
              Try again
            </MDSButton>
          ) : (
            <MDSButton
              variant={MDSButtonVariant.Contrast}
              onClick={handleImport}
              isDisabled={isLoading || !hasNotes || selectedCount === 0 || isImporting}
              isLoading={isImporting}
            >
              {isImporting
                ? "Importing..."
                : `Import ${selectedCount} ${pluralize(selectedCount, "note", "notes")}`}
            </MDSButton>
          )}
        </StyledFooter>
      </StyledWideDialog>
    </MDSModal>
  );
});

const StyledWideDialog = styled(MDSDialog)`
  width: min(1280px, 90vw);
  max-width: 90vw;
`;

const StyledContent = styled.div`
  position: relative;
  width: 100%;
  padding-left: ${({ theme }) => theme.spacing[3]};
`;

const StyledContentWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const StyledVirtuosoWrapper = styled.div`
  overflow: hidden;
`;

const StyledSectionHeader = styled.header`
  display: flex;
  align-items: start;
  gap: ${({ theme }) => theme.spacing["2"]};
  padding-top: ${({ theme }) => theme.spacing["4"]};
  padding-bottom: ${({ theme }) => theme.spacing["2"]};
  padding-inline: ${({ theme }) => theme.spacing["0"]};
  user-select: none;
  text-transform: uppercase;
  letter-spacing: 0.24px;

  label {
    text-transform: uppercase;
    letter-spacing: 0.24px;
    cursor: pointer;
  }
`;

const StyledFilterContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["3"]};
  width: 100%;
  padding-bottom: ${({ theme }) => theme.spacing["0.5"]};

  > :first-child {
    flex: 1;
  }
`;

const StyledFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const StyledFooterLeft = styled.div`
  flex: 1;
`;

const StyledCollectionOptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["2"]};
  width: 100%;
  margin-bottom: ${({ theme }) => theme.spacing["4"]};
`;

const StyledCollectionOptionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding-left: ${({ theme }) => theme.spacing["2"]};
`;

const StyledToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["2"]};
`;
